/**
 * Admin — AI Concierge Settings (V4.16.21)
 *
 * Backend control panel for the chatbot scope:
 *   • Capability toggles (mortgage calc, stamp duty, SPA docs, …)
 *   • Quick-action chip editor (customer + agent)
 *   • System-prompt preamble (extra instructions / persona override)
 *   • Model selector (GPT-4o / GPT-4o-mini / Claude Sonnet 4.5)
 *   • Test playground — sandboxed prompt-tester w/ role override.
 *
 * Only `super_admin` and `operation_admin` can access this page.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { chatSettingsAPI } from "../../lib/api";
import { toast } from "sonner";
import {
  Save, RotateCcw, Sparkles, Plus, Trash2, GripVertical, Send,
  Loader2, ArrowUp, ArrowDown, MessageCircle, Settings as SettingsIcon,
} from "lucide-react";

const CAPABILITY_META = {
  property_search:       { label: "Property Search & Recommendations", group: "Customer", description: "Suggest properties matching budget/location/type. Returns inline rich cards." },
  mortgage_calc:         { label: "Mortgage Affordability",            group: "Customer", description: "Step-by-step monthly installment + price ceiling using MY defaults (30-yr / 4.5% / 60% DSR)." },
  stamp_duty_calc:       { label: "Stamp Duty + Legal Fees",           group: "Customer", description: "MY 2024 bracketed scale (1% / 2% / 3% / 4%) + legal fees + loan stamp." },
  booking_status:        { label: "Booking & Appointment Status",      group: "Customer", description: "Look up the user's own bookings & viewings by ID or recency." },
  spa_documents:         { label: "SPA / Document Checklist",          group: "Customer", description: "Standard MY checklist (NRIC, payslips, EPF, EA, business-owner extras)." },
  perk_recommendations:  { label: "Perk Recommendations",              group: "Customer", description: "Personalised perk suggestions based on user's redemption history." },
  viewing_booking:       { label: "Schedule Property Viewing",         group: "Customer", description: "Auto-creates an appointment when the user confirms a viewing." },
  agent_commission:      { label: "Commission Pipeline Summary",       group: "Agent",    description: "Pending vs paid totals, claim status counts." },
  agent_pipeline:        { label: "Booking Pipeline by Status",        group: "Agent",    description: "Real-time booking count aggregation by status." },
  agent_sales_pitch:     { label: "Sales Pitch Generator",             group: "Agent",    description: "Tailored 3-paragraph pitch for a property + customer persona." },
  agent_referral:        { label: "Referral Share Helper",             group: "Agent",    description: "WhatsApp-ready message with the agent's actual referral code." },
};

const ICON_CHOICES = [
  "Home", "Calculator", "Tag", "Calendar", "FileText", "Wallet", "BarChart3",
  "Share2", "Sparkles", "MessageCircle", "MapPin", "Building2", "Search",
];

const emptyAction = () => ({ id: `qa_${Date.now()}`, icon: "Sparkles", label: "", prompt: "" });

const QuickActionEditor = ({ list, onChange, testid }) => {
  const update = (i, field, val) => {
    const next = [...list];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {list.map((qa, i) => (
        <div key={qa.id} className="rounded-xl border border-gray-200 p-3 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <GripVertical className="h-4 w-4 text-gray-400" />
            <Input
              value={qa.label}
              onChange={(e) => update(i, "label", e.target.value)}
              placeholder="Chip label (e.g. Mortgage calc)"
              className="font-medium text-sm"
              data-testid={`${testid}-label-${i}`}
            />
            <Select value={qa.icon} onValueChange={(v) => update(i, "icon", v)}>
              <SelectTrigger className="w-32 shrink-0" data-testid={`${testid}-icon-${i}`}>
                <SelectValue placeholder="Icon" />
              </SelectTrigger>
              <SelectContent>
                {ICON_CHOICES.map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down">
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => remove(i)} className="text-red-500 hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Textarea
            value={qa.prompt}
            onChange={(e) => update(i, "prompt", e.target.value)}
            placeholder="Prompt sent to AI when user taps this chip…"
            className="text-xs min-h-[60px]"
            data-testid={`${testid}-prompt-${i}`}
          />
        </div>
      ))}
      <Button
        onClick={() => onChange([...list, emptyAction()])}
        variant="outline"
        className="w-full gap-2 border-dashed"
        data-testid={`${testid}-add`}
      >
        <Plus className="h-4 w-4" /> Add quick action
      </Button>
    </div>
  );
};

export default function AdminAIConciergeSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [capabilityKeys, setCapabilityKeys] = useState([]);
  const [supportedModels, setSupportedModels] = useState([]);

  // Test playground state
  const [testMessage, setTestMessage] = useState("I earn RM 8,000/month. What can I afford?");
  const [testRole, setTestRole] = useState("customer");
  const [testReply, setTestReply] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await chatSettingsAPI.get();
        setSettings(res.data.settings);
        setCapabilityKeys(res.data.capability_keys);
        setSupportedModels(res.data.supported_models);
      } catch (e) {
        toast.error("Failed to load settings");
      } finally { setLoading(false); }
    })();
  }, []);

  const setField = (field, value) => setSettings((s) => ({ ...s, [field]: value }));
  const setCap = (k, v) =>
    setSettings((s) => ({ ...s, capabilities: { ...s.capabilities, [k]: v } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        capabilities: settings.capabilities,
        customer_quick_actions: settings.customer_quick_actions,
        agent_quick_actions: settings.agent_quick_actions,
        system_prompt_preamble: settings.system_prompt_preamble || "",
        model: settings.model,
      };
      const res = await chatSettingsAPI.update(payload);
      setSettings(res.data.settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all AI Concierge settings to defaults?")) return;
    try {
      const res = await chatSettingsAPI.reset();
      setSettings(res.data.settings);
      toast.success("Reset to defaults");
    } catch { toast.error("Reset failed"); }
  };

  const handleTest = async () => {
    if (!testMessage.trim() || testing) return;
    setTesting(true);
    setTestReply(null);
    try {
      const res = await chatSettingsAPI.test(testMessage, testRole);
      setTestReply(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const groupedCaps = useMemo(() => {
    const groups = { Customer: [], Agent: [] };
    capabilityKeys.forEach((k) => {
      const meta = CAPABILITY_META[k];
      if (meta) groups[meta.group].push(k);
    });
    return groups;
  }, [capabilityKeys]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-7 w-7 animate-spin text-[#4E50FF]" />
      </div>
    );
  }
  if (!settings) return null;

  const enabledCount = Object.values(settings.capabilities).filter(Boolean).length;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-ai-concierge-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] flex items-center justify-center shadow">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">AI Concierge Settings</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Control what SAM AI can do — toggle capabilities, edit quick actions, swap the model.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-2" data-testid="reset-btn">
            <RotateCcw className="h-4 w-4" /> Reset
          </Button>
          <Button
            onClick={handleSave} disabled={saving}
            className="gap-2 bg-gradient-to-r from-[#4E50FF] to-[#6366F1] text-white shadow"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* Status row */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500">Active capabilities</div>
            <div className="text-2xl font-bold text-[#4E50FF]">{enabledCount} / {capabilityKeys.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500">Active model</div>
            <div className="text-base font-semibold text-gray-900 truncate">{settings.model}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500">Quick actions</div>
            <div className="text-base font-semibold text-gray-900">
              {settings.customer_quick_actions.length} customer · {settings.agent_quick_actions.length} agent
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="capabilities">
        <TabsList className="mb-4">
          <TabsTrigger value="capabilities" data-testid="tab-capabilities">Capabilities</TabsTrigger>
          <TabsTrigger value="quick_actions" data-testid="tab-quick_actions">Quick Actions</TabsTrigger>
          <TabsTrigger value="advanced" data-testid="tab-advanced">Advanced</TabsTrigger>
          <TabsTrigger value="playground" data-testid="tab-playground">Test Playground</TabsTrigger>
        </TabsList>

        {/* ─── Capabilities ──────────────────────────────────────── */}
        <TabsContent value="capabilities">
          <Card>
            <CardHeader>
              <CardTitle>What can SAM do?</CardTitle>
              <CardDescription>
                Toggle individual capabilities. Disabled capabilities are removed from the system
                prompt — the AI will gracefully decline if a user asks about them.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(groupedCaps).map(([group, keys]) => (
                <div key={group}>
                  <h3 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                    {group} capabilities
                    <Badge variant="secondary" className="text-[10px]">
                      {keys.filter((k) => settings.capabilities[k]).length} / {keys.length} enabled
                    </Badge>
                  </h3>
                  <div className="space-y-2">
                    {keys.map((k) => {
                      const meta = CAPABILITY_META[k];
                      return (
                        <div key={k} className="flex items-start justify-between gap-4 p-3 rounded-xl border border-gray-100 hover:bg-gray-50/50 transition">
                          <div className="flex-1 min-w-0">
                            <Label className="font-medium text-sm cursor-pointer">{meta?.label || k}</Label>
                            <p className="text-xs text-gray-500 mt-0.5">{meta?.description}</p>
                          </div>
                          <Switch
                            checked={!!settings.capabilities[k]}
                            onCheckedChange={(v) => setCap(k, v)}
                            data-testid={`cap-${k}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Quick Actions ─────────────────────────────────────── */}
        <TabsContent value="quick_actions">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer chips</CardTitle>
                <CardDescription>Shown to customers on the empty-state screen.</CardDescription>
              </CardHeader>
              <CardContent>
                <QuickActionEditor
                  list={settings.customer_quick_actions}
                  onChange={(v) => setField("customer_quick_actions", v)}
                  testid="customer-qa"
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Agent chips</CardTitle>
                <CardDescription>Shown to agents on the empty-state screen.</CardDescription>
              </CardHeader>
              <CardContent>
                <QuickActionEditor
                  list={settings.agent_quick_actions}
                  onChange={(v) => setField("agent_quick_actions", v)}
                  testid="agent-qa"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── Advanced ──────────────────────────────────────────── */}
        <TabsContent value="advanced">
          <Card>
            <CardHeader>
              <CardTitle>Advanced configuration</CardTitle>
              <CardDescription>Model selection and a custom prompt preamble for power users.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="font-medium">LLM Model</Label>
                <Select value={settings.model} onValueChange={(v) => setField("model", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedModels.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1.5">
                  Higher-tier models = better reasoning but higher cost per message. All use the Emergent LLM Key — no separate API keys needed.
                </p>
              </div>

              <Separator />

              <div>
                <Label className="font-medium">System prompt preamble (optional)</Label>
                <Textarea
                  value={settings.system_prompt_preamble || ""}
                  onChange={(e) => setField("system_prompt_preamble", e.target.value)}
                  placeholder="e.g. ALWAYS sign off with — SAM Concierge ✨\nUse Bahasa Malaysia when the user writes in BM."
                  className="mt-1.5 min-h-[140px] font-mono text-xs"
                  maxLength={4000}
                  data-testid="preamble-input"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Prepended to the baked-in system prompt. Useful for tone overrides, multilingual hints, or temporary campaigns. Max 4000 chars.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Test Playground ───────────────────────────────────── */}
        <TabsContent value="playground">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-[#4E50FF]" /> Test playground
              </CardTitle>
              <CardDescription>
                Send a sandboxed message using the CURRENT settings. Nothing is persisted — no user session is touched. Save your changes first to test the live config.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Test as role</Label>
                  <Select value={testRole} onValueChange={setTestRole}>
                    <SelectTrigger className="mt-1.5" data-testid="test-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleTest} disabled={testing || !testMessage.trim()}
                    className="w-full gap-2 bg-gradient-to-r from-[#4E50FF] to-[#6366F1] text-white"
                    data-testid="test-send-btn">
                    {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send test
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-sm">Message</Label>
                <Textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="mt-1.5 min-h-[80px]"
                  data-testid="test-message-input"
                />
              </div>

              {testReply && (
                <div className="rounded-xl border border-[#E5E7FF] bg-[#F8FAFF] p-4 space-y-3" data-testid="test-reply">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">Model: {testReply.model}</Badge>
                    <Badge variant="outline" className="text-[10px]">Prompt: {testReply.system_prompt_chars} chars</Badge>
                  </div>
                  <Separator />
                  <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {testReply.ai_response.content}
                  </div>
                  {testReply.ai_response.mentioned_properties?.length > 0 && (
                    <div className="text-xs text-gray-500 pt-2 border-t">
                      Inline property cards: {testReply.ai_response.mentioned_properties.map(p => p.name).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
